// ABOUTME: Utility hooks, constants, and helpers for the Country Configurations feature.
// ABOUTME: Contains useSelectedScope, useCountryName, useShippingTypeFilter, shared scope level options, and error utilities.
import {useCallback, useEffect, useRef, useState} from 'react'


import {mdiAccountOutline, mdiClipboardTextClockOutline, mdiEarth, mdiMapMarkerRadius, mdiOfficeBuilding, mdiTruckOutline} from '@mdi/js'
import {useHistory, useLocation} from 'react-router-dom'

import {getCountryFullName} from '../../../common/utils/utils'
import {useTranslation} from '../../../hooks/useTranslation'
import {BUSINESS_LINE_SHIPPING_TYPES} from './constants'
import {ShippingType, ScopeLevel} from './types'
import {DeliveryWindowConfig, DeliveryWindowFormValues} from './types/deliveryWindow'

/** Explicit slug overrides for entity IDs whose auto-converted slug differs from the backend route. */
const ENTITY_SLUG_OVERRIDES: Partial<Record<string, string>> = {
  orderIntakeLeadTime: 'order-intake-lead-times',
  orderCancellationLeadTime: 'order-cancellation-lead-times',
  orderChangeLeadTime: 'order-change-lead-times',
}

/** Converts a camelCase entity ID to a kebab-case URL slug. */
export const toEntitySlug = (entityId: string): string =>
  ENTITY_SLUG_OVERRIDES[entityId] ?? entityId.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()

const SLUG_TO_ENTITY_ID: Record<string, string> = {
  'unloading-method': 'unloadingMethod',
  'delivery-window': 'deliveryWindow',
  'operational-hours': 'operationalHours',
  'order-intake-lead-times': 'orderIntakeLeadTime',
  'order-cancellation-lead-times': 'orderCancellationLeadTime',
  'order-change-lead-times': 'orderChangeLeadTime',
  'overbooking-allowance': 'overbookingAllowance',
  'pump-configuration': 'pumpConfiguration',
  'load-spacing': 'loadSpacing',
}

/** Converts a kebab-case URL slug back to the original entity ID. */
export const fromEntitySlug = (slug: string): string =>
  SLUG_TO_ENTITY_ID[slug] ?? slug

const SCOPE_QUERY_PARAM = 'scope'

export const SCOPE_LEVEL_OPTIONS: {value: ScopeLevel; labelKey: string; tooltipKey: string; icon: string; iconClassName: string}[] = [
  {value: 'country', labelKey: 'countryConfigurations.entity.scopeNavigator.levelCountry', tooltipKey: 'countryConfigurations.overview.tooltipLevelCountry', icon: mdiEarth, iconClassName: 'text-foreground_faded'},
  {value: 'salesOrg', labelKey: 'countryConfigurations.entity.scopeNavigator.levelSalesOrg', tooltipKey: 'countryConfigurations.overview.tooltipLevelSalesOrg', icon: mdiOfficeBuilding, iconClassName: 'text-info'},
  {value: 'dispatchGr', labelKey: 'countryConfigurations.entity.scopeNavigator.levelDispatchGr', tooltipKey: 'countryConfigurations.overview.tooltipLevelDispatchGr', icon: mdiClipboardTextClockOutline, iconClassName: 'text-accent_1'},
  {value: 'dispatchCenter', labelKey: 'countryConfigurations.entity.scopeNavigator.levelDispatchCenter', tooltipKey: 'countryConfigurations.overview.tooltipLevelDispatchCenter', icon: mdiMapMarkerRadius, iconClassName: 'text-negative'},
  {value: 'plant', labelKey: 'countryConfigurations.entity.scopeNavigator.levelPlant', tooltipKey: 'countryConfigurations.overview.tooltipLevelPlant', icon: mdiTruckOutline, iconClassName: 'text-positive'},
  {value: 'customer', labelKey: 'countryConfigurations.entity.scopeNavigator.levelCustomer', tooltipKey: 'countryConfigurations.overview.tooltipLevelCustomer', icon: mdiAccountOutline, iconClassName: 'text-accent_5'}
]  

export const useSelectedScope = () => {
  const history = useHistory()
  const location = useLocation()

  const selectedScopeId =
    new URLSearchParams(location.search).get(SCOPE_QUERY_PARAM)

  const handleScopeSelect = useCallback((id: string) => {
    const params = new URLSearchParams(location.search)
    params.set(SCOPE_QUERY_PARAM, id)
    history.replace({search: params.toString()})
  }, [history, location.search])

  return {selectedScopeId, handleScopeSelect}
}

export const useCountryName = (countryId: string) => getCountryFullName(countryId)

const SHIPPING_TYPE_QUERY_PARAM = 'shippingType'

const SHIPPING_TYPES: readonly ShippingType[] = ['bagged-deliver', 'bagged-collect', 'bulk-deliver', 'bulk-collect']

const isShippingType = (value: string | undefined): value is ShippingType =>
  value !== undefined && (SHIPPING_TYPES as readonly string[]).includes(value)

export const useShippingTypeFilter = (businessLine: string | undefined) => {
  const {t} = useTranslation()
  const history = useHistory()
  const location = useLocation()

  const businessLineShippingTypes = businessLine ? BUSINESS_LINE_SHIPPING_TYPES[businessLine] : undefined
  const shippingOptions = businessLineShippingTypes?.map((st) => ({
    value: st.value,
    label: t(st.labelKey),
  }))

  const shippingTypeFromUrl = new URLSearchParams(location.search).get(SHIPPING_TYPE_QUERY_PARAM) ?? undefined
  const fallback = businessLineShippingTypes?.[0]?.value
  const shippingType: ShippingType | undefined =
    isShippingType(shippingTypeFromUrl) && shippingOptions?.some((o) => o.value === shippingTypeFromUrl)
      ? shippingTypeFromUrl
      : isShippingType(fallback) ? fallback : undefined

  const handleShippingTypeChange = (value: string) => {
    const params = new URLSearchParams(location.search)
    params.set(SHIPPING_TYPE_QUERY_PARAM, value)
    history.replace({search: params.toString()})
  }

  return {shippingType, shippingOptions, handleShippingTypeChange}
}

/**
 * Formats a duration in minutes as "H:MM" string.
 * Example: 90 → "1:30", 30 → "0:30", 60 → "1:00"
 */
export function formatMinutesAsTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

/**
 * Parses a "H:MM" string back to minutes.
 * Returns undefined if the string is not a valid time format.
 * Example: "1:30" → 90, "0:30" → 30
 */
export function parseTimeToMinutes(display: string): number | undefined {
  const match = display.match(/^(\d+):(\d{0,2})$/)
  if (!match) return undefined
  const h = parseInt(match[1], 10)
  const m = parseInt(match[2] || '0', 10)
  if (m >= 60) return undefined
  return h * 60 + m
}

/** Validates time ranges — returns error map keyed by `${rowIndex}.${columnKey}.start|end` */
export function validateHours(
  rows: Array<{timeRanges: Record<string, {start: string; end: string}>}>,
  t: (key: string) => string,
  options?: {requiredColumns?: string[]}
): Record<string, string> {
  const errors: Record<string, string> = {}
  rows.forEach((row, rowIndex) => {
    // Phase 1: required + ordering per column
    Object.entries(row.timeRanges).forEach(([colKey, range]) => {
      const hasStart = range.start.length > 0
      const hasEnd = range.end.length > 0
      if (!hasStart && !hasEnd && options?.requiredColumns?.includes(colKey)) {
        errors[`${rowIndex}.${colKey}.start`] = t('countryConfigurations.deliveryWindow.validation.required')
        errors[`${rowIndex}.${colKey}.end`] = t('countryConfigurations.deliveryWindow.validation.required')
      }
      if (hasStart && !hasEnd) {
        errors[`${rowIndex}.${colKey}.end`] = t('countryConfigurations.deliveryWindow.validation.required')
      }
      if (!hasStart && hasEnd) {
        errors[`${rowIndex}.${colKey}.start`] = t('countryConfigurations.deliveryWindow.validation.required')
      }
      if (hasStart && hasEnd && range.end <= range.start) {
        errors[`${rowIndex}.${colKey}.start`] = t('countryConfigurations.deliveryWindow.validation.startMustBeEarlier')
        errors[`${rowIndex}.${colKey}.end`] = t('countryConfigurations.deliveryWindow.validation.endMustBeLater')
      }
    })

    // Phase 2: cross-column containment (only when online range is complete and valid)
    const online = row.timeRanges.onlineDelivery
    if (!online || !online.start || !online.end || online.end <= online.start) return

    const checkContainment = (colKey: string, errorMsg: string) => {
      const range = row.timeRanges[colKey]
      if (!range || !range.start || !range.end || range.end <= range.start) return
      const startKey = `${rowIndex}.${colKey}.start`
      const endKey = `${rowIndex}.${colKey}.end`
      if (!errors[startKey] && range.start < online.start) errors[startKey] = errorMsg
      if (!errors[endKey] && range.end > online.end) errors[endKey] = errorMsg
    }

    checkContainment('defaultDelivery', t('countryConfigurations.deliveryWindow.validation.defaultWithinOnline'))
    checkContainment('slotManagement', t('countryConfigurations.deliveryWindow.validation.slotMgmtWithinOnline'))
  })
  return errors
}

/** Extract form-editable fields from a config object */
export const toFormValues = (cfg: DeliveryWindowConfig): DeliveryWindowFormValues => ({
  timeZone: cfg.timeZone,
  businessDays: cfg.businessDays,
  slotManagementDays: cfg.slotManagementDays,
  standardOperatingDays: cfg.standardOperatingDays,
  standardHours: cfg.standardHours,
  nonStandardHours: cfg.nonStandardHours,
  deliveryConstraints: cfg.deliveryConstraints,
})

/**
 * Validates single-value operating hours rows (opens / prePlanning / closes).
 * Returns error map keyed by `${rowIndex}.${columnKey}`.
 */
export function validateSingleHours(
  rows: Array<{key: string; values: {opens: string; prePlanning: string; closes: string}}>,
  t: (key: string) => string,
): Record<string, string> {
  const errors: Record<string, string> = {}
  rows.forEach((row, rowIndex) => {
    const {opens, prePlanning, closes} = row.values
    if (!opens) errors[`${rowIndex}.opens`] = t('countryConfigurations.operationalHours.validation.required')
    if (!prePlanning) errors[`${rowIndex}.prePlanning`] = t('countryConfigurations.operationalHours.validation.required')
    if (!closes) errors[`${rowIndex}.closes`] = t('countryConfigurations.operationalHours.validation.required')
    const o = parseTimeToMinutes(opens)
    const p = parseTimeToMinutes(prePlanning)
    const c = parseTimeToMinutes(closes)
    if (o !== undefined && c !== undefined && o >= c) {
      errors[`${rowIndex}.opens`] = t('countryConfigurations.operationalHours.validation.opensMustBeBeforeCloses')
      errors[`${rowIndex}.closes`] = t('countryConfigurations.operationalHours.validation.closesMustBeAfterOpens')
    }
    if (p !== undefined && ((o !== undefined && p <= o) || (c !== undefined && p >= c))) {
      errors[`${rowIndex}.prePlanning`] = t('countryConfigurations.operationalHours.validation.prePlanningOutOfRange')
    }
  })
  return errors
}

/**
 * Encapsulates row-level touch tracking for table forms.
 * Auto-focuses the new row's first input on add.
 * Exposes helpers to mark/remove touched rows and generate unique row IDs.
 *
 * @param rowsLength - reactive row count; drives the auto-focus effect.
 * @param initialCount - starting value for the row ID counter (use when the component pre-seeds rows before calling the hook).
 */
export function useRowTouchTracking(rowsLength: number, initialCount = 0) {
  const rowIdCounter = useRef(initialCount)
  const nextRowId = useCallback(() => String(rowIdCounter.current++), [])
  const justAddedRef = useRef(false)
  const newRowRef = useRef<HTMLDivElement>(null)
  const [touchedRows, setTouchedRows] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!justAddedRef.current) return
    justAddedRef.current = false
    const input = newRowRef.current?.querySelector<HTMLInputElement>('input')
    input?.focus()
  }, [rowsLength])

  const markTouched = useCallback((rowId: string) => {
    setTouchedRows((prev) => new Set(prev).add(rowId))
  }, [])

  const removeTouched = useCallback((rowId: string) => {
    setTouchedRows((prev) => {
      const next = new Set(prev)
      next.delete(rowId)
      return next
    })
  }, [])

  const markAdded = useCallback(() => {
    justAddedRef.current = true
  }, [])

  return {touchedRows, markTouched, removeTouched, newRowRef, nextRowId, markAdded}
}

/** Returns true when a save was rejected because another user changed the same configuration first (HTTP 409). */
export const isConflictError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const e = error as {isAxiosError?: boolean; response?: {status?: number}}
  return e.isAxiosError === true && e.response?.status === 409
}
